import { useEffect } from "react";
import { FrappeProvider, useFrappeAuth, useFrappeGetCall } from "frappe-react-sdk";
import { BrowserRouter, useLocation } from "react-router-dom";

import { AppToaster } from "./components/AppToaster";
import { PushNotificationSync } from "./components/PushNotificationSync";
import { persistLanguage } from "./lib/i18n";
import i18n from "./lib/i18n";
import { API_BASE_URL, ROUTER_BASENAME, SITE_NAME, SOCKET_PORT } from "./lib/constants";
import { isMarketingPath } from "./marketing/content/routes";
import { AppRouter } from "./router";
import type { FrappeMessage, UserProfile } from "./lib/types";

function getSiteName(): string | undefined {
  const maybeFrappe = window as typeof window & {
    frappe?: { boot?: { sitename?: string; versions?: { frappe?: string } } };
  };
  const version = maybeFrappe.frappe?.boot?.versions?.frappe || "";
  if (version.startsWith("15") || version.startsWith("16")) {
    return maybeFrappe.frappe?.boot?.sitename || SITE_NAME;
  }
  return SITE_NAME;
}

function ensureCsrfToken(): void {
  const maybeWindow = window as typeof window & {
    csrf_token?: string;
    frappe?: { csrf_token?: string; boot?: { csrf_token?: string } };
  };

  const metaToken =
    typeof document !== "undefined"
      ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
      : "";

  const resolved =
    maybeWindow.csrf_token ||
    maybeWindow.frappe?.csrf_token ||
    maybeWindow.frappe?.boot?.csrf_token ||
    metaToken ||
    "";

  if (resolved && !maybeWindow.csrf_token) {
    maybeWindow.csrf_token = resolved;
  }
}

function LanguageSync() {
  const { pathname } = useLocation();
  const { currentUser } = useFrappeAuth();
  const profileCall = useFrappeGetCall<FrappeMessage<UserProfile>>(
    "ownerflow.api.user.get_user_profile",
    undefined,
    currentUser ? "boot:user-profile" : null,
    undefined,
    "POST",
  );

  useEffect(() => {
    if (isMarketingPath(pathname)) return;
    const lang = profileCall.data?.message?.language;
    if (lang && lang !== i18n.language) {
      persistLanguage(lang);
      void i18n.changeLanguage(lang);
    }
  }, [pathname, profileCall.data?.message?.language]);

  return null;
}

function App() {
  ensureCsrfToken();
  const csrfToken = (window as typeof window & { csrf_token?: string }).csrf_token || "";
  const customHeaders = csrfToken ? { "X-Frappe-CSRF-Token": csrfToken } : undefined;

  return (
    <FrappeProvider
      url={API_BASE_URL}
      siteName={getSiteName()}
      socketPort={csrfToken ? SOCKET_PORT : undefined}
      customHeaders={customHeaders}
    >
      <BrowserRouter basename={ROUTER_BASENAME || undefined}>
        <LanguageSync />
        <PushNotificationSync />
        <AppRouter />
        <AppToaster />
      </BrowserRouter>
    </FrappeProvider>
  );
}

export default App;
